import type { CapabilityState, EvaluationResult, Evidence, TechnologyNode } from "./model";

export const capabilityRank: Record<CapabilityState, number> = {
  DISCOVERED: 0,
  LEARNING: 1,
  UNDERSTOOD: 2,
  BUILT: 3,
  SHIPPED: 4,
  EVIDENCED: 5,
  DEFENDED: 6,
};

const nextState: Record<CapabilityState, CapabilityState> = {
  DISCOVERED: "LEARNING",
  LEARNING: "UNDERSTOOD",
  UNDERSTOOD: "BUILT",
  BUILT: "SHIPPED",
  SHIPPED: "EVIDENCED",
  EVIDENCED: "DEFENDED",
  DEFENDED: "DEFENDED",
};

export function evaluateCapability(node: TechnologyNode, selectedEvidence: Evidence[]): EvaluationResult {
  const matching = selectedEvidence.filter((item) => item.nodeIds.includes(node.id));
  const verified = matching.filter((item) => item.verification !== "unverified");
  const types = new Set(verified.map((item) => item.type));
  const reasons: string[] = [];
  let eligible = false;

  if (node.capabilityState === "DEFENDED") {
    reasons.push("DEFENDED is terminal until a new capability-state model is approved.");
  } else if (node.capabilityState === "SHIPPED") {
    eligible = verified.length >= 2 && types.has("test") && (types.has("deploy") || types.has("pr"));
    reasons.push(
      eligible
        ? "Verified test evidence plus deploy/PR evidence satisfy the EVIDENCED gate."
        : "EVIDENCED requires verified test evidence and verified deploy or PR evidence.",
    );
  } else if (node.capabilityState === "EVIDENCED") {
    eligible = types.has("interview") || types.has("incident") || verified.some((item) => item.verification === "external");
    reasons.push(
      eligible
        ? "External pressure-test evidence satisfies the DEFENDED gate."
        : "DEFENDED requires an external review, interview, incident or equivalent pressure test.",
    );
  } else {
    eligible = verified.length > 0;
    reasons.push(
      eligible
        ? "At least one verified artifact supports the next bounded state transition."
        : "No verified evidence supports advancing this capability.",
    );
  }

  return {
    nodeId: node.id,
    currentState: node.capabilityState,
    recommendation: eligible ? nextState[node.capabilityState] : node.capabilityState,
    eligible,
    reasons,
    evidenceConsidered: matching.length,
  };
}
